import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import CustomerEnquiries from "../../layout/builders/CustomerEnquiry/CustomerEnquiries";
import PostedProperties from "../../layout/realestateagent/HousingPost/PostedProperties";
import Layout from "../../layout/builders/Layout";
import { axiosInstancev1 } from "../../lib/axiosInstance";
import toast from "react-hot-toast";
import RichTextEditor from "../../components/common/RichTextEditor";
import DurationSelectionModal from "../../layout/realestateagent/HousingPost/DurationSelectionModal";
import LocationPicker from "../../components/LocationPicker";
import {
  BuildingOffice2Icon,
  HomeModernIcon,
  HomeIcon,
  MapPinIcon,
  BanknotesIcon,
  ArrowsPointingOutIcon,
  ShieldCheckIcon,
  PhotoIcon,
  BoltIcon,
  UserIcon,
  PhoneIcon,
  EnvelopeIcon,
  ClockIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";

const TOTAL_STEPS = 6;

const STEPS_META = [
  {
    id: 1,
    label: "Basic & Approvals",
    title: "Basic Details & Approvals",
    desc: "Core info and approval status.",
  },
  {
    id: 2,
    label: "Specifications",
    title: "Plot Specifications",
    desc: "Area, facing, and specific options.",
  },
  {
    id: 3,
    label: "Media",
    title: "Media Upload",
    desc: "Add photos and layout maps.",
  },
  {
    id: 4,
    label: "Additional",
    title: "Additional Information",
    desc: "Explain nearby facilities and advantages.",
  },
  {
    id: 5,
    label: "Location",
    title: "Location Details",
    desc: "Share accurate address and survey information.",
  },
  {
    id: 6,
    label: "Contact",
    title: "Contact Details",
    desc: "Let buyers know how to reach you.",
  },
];

// Re-add helper components that were removed by chunk replacement
const GradientHeader = ({ step, totalSteps }) => (
  <div className="rounded-3xl bg-gradient-to-r from-blue-50 via-sky-100 to-blue-100 px-5 py-4">
    <div className="flex items-center justify-between gap-3">
      <div className="flex-1">
        <p className="text-xs uppercase tracking-wide text-blue-700">
          Step {step} of {totalSteps}
        </p>
        <div className="mt-1 h-1.5 w-full bg-white/60 rounded-full overflow-hidden">
          <div
            className="h-full bg-blue-500 rounded-full transition-all duration-300"
            style={{ width: `${(step / totalSteps) * 100}%` }}
          />
        </div>
      </div>
    </div>
  </div>
);

const StepCard = ({ stepNumber, title, description, children, isOpen, toggle }) => {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm transition-all duration-300 hover:shadow-md hover:border-blue-100 hover:-translate-y-[1px]">
      <button
        type="button"
        onClick={toggle}
        className="w-full flex items-center justify-between px-4 py-3 sm:px-5 sm:py-4"
      >
        <div className="text-left">
          <h2 className="text-sm sm:text-base font-semibold text-gray-900">
            {title}
          </h2>
          {description && (
            <p className="text-xs text-gray-500 mt-0.5">{description}</p>
          )}
        </div>
        <ChevronDownIcon className={`w-4 h-4 text-gray-500 transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`} />
      </button>
      <div className={`border-t border-gray-100 overflow-hidden transition-all duration-300 ${isOpen ? "max-h-[1200px] opacity-100" : "max-h-0 opacity-0"}`}>
        <div className="px-4 py-4 sm:px-5 sm:py-5">{children}</div>
      </div>
    </div>
  );
};

const LISTING_TYPES = ["Sale"];
const PLOT_AREA_UNITS = ["Sq.ft", "Cent", "Acre", "Hectare", "Ground"];
const YES_NO = ["Yes", "No"];

const PLOT_TYPES = [
  { id: "Residential Plot", label: "Residential Plot", icon: HomeIcon },
  { id: "Mixed-Use Plot", label: "Mixed-Use Plot", icon: BuildingOffice2Icon },
  { id: "Commercial Plot", label: "Commercial Plot", icon: BuildingOffice2Icon },
];

const LAND_TYPES = [
  { id: "Residential Land", label: "Residential Land", icon: HomeIcon },
  { id: "Commercial Land", label: "Commercial Land", icon: BuildingOffice2Icon },
  { id: "Industrial Land", label: "Industrial Land", icon: BuildingOffice2Icon },
  { id: "Agricultural Land", label: "Agricultural Land", icon: BuildingOffice2Icon },
  { id: "Farm Land", label: "Farm Land", icon: HomeIcon },
];

const APPROVAL_FIELDS = [
  { id: "dtcpApproval", label: "DTCP Approval" },
  { id: "cmdaApproval", label: "CMDA Approval" },
  { id: "pattaChitta", label: "Patta / Chitta" },
  { id: "fmbSketch", label: "FMB Sketch" },
  { id: "landTaxReceipt", label: "Land Tax Receipt" },
  { id: "layoutPdf", label: "Layout PDF" },
];

const PropertyPost = ({ plotMode }) => {
  // ... existing PropertyPost code ...

  const [activeTab, setActiveTab] = useState("post");
  const [step, setStep] = useState(1);

  const [form, setForm] = useState({
    // 1. Basic Details
    plotTitle: "",
    listingType: "",
    plotType: "",
    price: "",

    // 2. Location
    city: "",
    locality: "",
    village: "",
    surveyNumber: "",
    plotAddress: "",
    pincode: "",
    mapPin: "",
    latitude: null,
    longitude: null,

    // 3. Plot Specifications
    plotAreaValue: "",
    plotAreaUnit: "Sq ft",
    plotFacing: "",
    specificationOption: "", // New field: Road side, Back side, etc.
    cornerPlot: "No",
    gatedCommunity: "No",
    boundaryWall: "No",

    // 4. Approvals & Documents (Merged into Step 1 visually, but kept in state)
    dtcpApproval: "No",
    cmdaApproval: "No",
    reraNumber: "",
    pattaChitta: "No",
    fmbSketch: "No",
    landTaxReceipt: "No",
    layoutPdf: "No",

    // 6. Additional Information
    nearbyFacilities: "",
    distanceToMainRoad: "",
    soilType: "",
    advantages: "",

    // 7. Contact
    ownerName: "",
    mobileNumber: "",
    email: "",
    preferredVisitTime: "",
  });

  // 5. Media Upload
  const [media, setMedia] = useState({
    plotPhotos: [],
    layoutMap: [],
  });

  const [errors, setErrors] = useState({});
  const [openSections, setOpenSections] = useState({
    1: true,
    2: true,
    3: true,
    4: true,
    5: true,
    6: true,
    7: true,
  });
  const [showDurationModal, setShowDurationModal] = useState(false);
  const [createdListingId, setCreatedListingId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [deletedImageIds, setDeletedImageIds] = useState([]);
  const navigate = useNavigate();

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const handleMediaUpload = (key, files) => {
    const fileArr = Array.from(files || []);
    if (!fileArr.length) return;

    const validFiles = [];
    fileArr.forEach((file) => {
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`File ${file.name} exceeds 10MB limit.`);
        return;
      }
      validFiles.push(file);
    });

    if (validFiles.length === 0) return;

    const mapped = validFiles.map((file) => ({
      file,
      previewUrl: URL.createObjectURL(file),
    }));
    setMedia((prev) => ({
      ...prev,
      [key]: [...prev[key], ...mapped],
    }));
  };

  const removeMedia = (key, index) => {
    const itemToRemove = media[key][index];
    if (itemToRemove && itemToRemove.existing && itemToRemove.imageId) {
      setDeletedImageIds((prev) => [...prev, itemToRemove.imageId]);
    }
    setMedia((prev) => ({
      ...prev,
      [key]: prev[key].filter((_, i) => i !== index),
    }));
  };

  const setAsMain = (key, index) => {
    if (index === 0) return;
    setMedia((prev) => {
      const newArr = [...prev[key]];
      const selected = newArr.splice(index, 1)[0];
      newArr.unshift(selected);
      return {
        ...prev,
        [key]: newArr,
      };
    });
  };

  const validateStep = () => {
    const newErrors = {};

    if (step === 1) {
      if (!form.plotTitle.trim()) newErrors.plotTitle = "Plot title is required";
      if (!form.listingType) newErrors.listingType = "Choose a listing type";
      if (!form.plotType) newErrors.plotType = "Select at least one plot type";
      if (!form.price) newErrors.price = "Price is required";
    }

    if (step === 2) { // Specifications
      if (!form.plotAreaValue) newErrors.plotAreaValue = "Plot area is required";
      if (!form.plotFacing.trim()) newErrors.plotFacing = "Plot facing is required";
    }

    if (step === 3) { // Media
      // Media validation if needed
    }

    if (step === 5) { // Location
      if (!form.city.trim()) newErrors.city = "City is required";
      if (!form.locality.trim()) newErrors.locality = "Locality is required";
      if (!form.plotAddress.trim()) newErrors.plotAddress = "Plot address is required";
    }

    if (step === 6) { // Contact
      if (!form.ownerName.trim())
        newErrors.ownerName = "Owner name is required";
      if (!form.mobileNumber.trim())
        newErrors.mobileNumber = "Mobile number is required";
      if (!form.email.trim()) newErrors.email = "Email is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };


  const { id } = useParams();
  const [editingId, setEditingId] = useState(null);

  useEffect(() => {
    if (id) {
      handleEdit({ id });
    }
  }, [id]);

  const handleEdit = async (listing) => {
    try {
      setSubmitting(true);
      const response = await axiosInstancev1.get(`/properties/${listing.id}`);
      const data = response.data;

      if (data.success) {
        const details = data.details || {};

        // Parse amenities/facilities if they are strings
        let facilities = details.nearby_facilities;
        if (typeof facilities === 'string') {
          try { facilities = JSON.parse(facilities); } catch (e) { }
        }

        setForm({
          // 1. Basic Details
          plotTitle: details.plot_title || "",
          listingType: data.listing_type ? data.listing_type.charAt(0).toUpperCase() + data.listing_type.slice(1).toLowerCase() : "",
          plotType: details.plot_type || "",
          price: details.price || "",

          // 2. Location
          city: data.city || "",
          locality: data.locality || "",
          village: data.landmark || "",
          surveyNumber: details.survey_number || "",
          plotAddress: data.street_address || "",
          pincode: data.pincode || "",
          mapPin: data.google_map_link || "",
          latitude: data.latitude || null,
          longitude: data.longitude || null,

          // 3. Plot Specifications
          plotAreaValue: details.plot_area || "",
          plotAreaUnit: (details.plot_area_unit || "Sq ft"),
          plotFacing: details.facing_direction || "",
          cornerPlot: details.is_corner_plot ? "Yes" : "No",
          gatedCommunity: details.is_gated_community ? "Yes" : "No",
          boundaryWall: details.has_boundary_wall ? "Yes" : "No",

          // 4. Approvals & Documents
          dtcpApproval: details.dtcp_approved ? "Yes" : "No",
          cmdaApproval: details.cmda_approved ? "Yes" : "No",
          reraNumber: details.rera_number || "",
          pattaChitta: details.patta_available ? "Yes" : "No",
          fmbSketch: details.fmb_sketch_available ? "Yes" : "No",
          landTaxReceipt: details.tax_receipt_available ? "Yes" : "No",
          layoutPdf: "No",

          // 6. Additional Information
          nearbyFacilities: typeof facilities === 'string' ? facilities : (JSON.stringify(facilities) || ""),
          distanceToMainRoad: details.distance_to_main_road || "",
          soilType: details.soil_type || "",
          advantages: details.advantages || "",

          // 7. Contact
          ownerName: data.contact_name || "",
          mobileNumber: data.contact_mobile || "",
          email: data.contact_email || "",
          preferredVisitTime: data.contact_time || "",
        });

        setEditingId(listing.id);
        setActiveTab("post");
        setStep(1);

        if (data.images && Array.isArray(data.images)) {
          setMedia(prev => ({
            ...prev,
            plotPhotos: data.images.map(img => ({
              previewUrl: img.image_url,
              file: null,
              existing: true,
              imageId: img.id
            })),
            droneView: [],
            layoutMap: []
          }));
        } else {
          setMedia({
            plotPhotos: [],
            droneView: [],
            layoutMap: []
          });
        }
      }
    } catch (error) {
      console.error("Error fetching listing details:", error);
      toast.error("Failed to load property details");
    } finally {
      setSubmitting(false);
    }
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm({
      plotTitle: "", listingType: "", plotType: "", price: "",
      city: "", locality: "", village: "", surveyNumber: "", plotAddress: "", pincode: "", mapPin: "",
      plotAreaValue: "", plotAreaUnit: "Sq ft", plotFacing: "", cornerPlot: "No", gatedCommunity: "No", boundaryWall: "No",
      dtcpApproval: "No", cmdaApproval: "No", reraNumber: "", pattaChitta: "No", fmbSketch: "No", landTaxReceipt: "No", layoutPdf: "No",
      nearbyFacilities: "", distanceToMainRoad: "", soilType: "", advantages: "",
      ownerName: "", mobileNumber: "", email: "", preferredVisitTime: "",
    });
    setMedia({
      plotPhotos: [],
      droneView: [],
      layoutMap: [],
    });
    setStep(1);
    navigate('/builder/propertypost');
  };


  const goNext = () => {
    if (!validateStep()) return;
    setStep((prev) => Math.min(prev + 1, TOTAL_STEPS));
  };

  const goBack = () => {
    setStep((prev) => Math.max(prev - 1, 1));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateStep()) return;

    setSubmitting(true);

    try {
      // Prepare form data
      const formData = new FormData();

      // Basic fields
      formData.append("listing_type", form.listingType);
      formData.append("property_category", "plot");
      formData.append("city", form.city);
      formData.append("locality", form.locality);
      formData.append("street_address", form.plotAddress);
      formData.append("landmark", form.village || "");
      formData.append("pincode", form.pincode || "");
      formData.append("google_map_link", form.mapPin || "");

      if (form.latitude) formData.append("latitude", form.latitude);
      if (form.longitude) formData.append("longitude", form.longitude);

      // Contact details
      formData.append("contact_name", form.ownerName);
      formData.append("contact_mobile", form.mobileNumber);
      formData.append("contact_alt_mobile", "");
      formData.append("contact_email", form.email);
      formData.append("contact_time", form.preferredVisitTime || "");

      // Plot details
      const plotDetails = {
        plot_type: form.plotType,
        transaction_type: "",
        price: form.price,
        plot_title: form.plotTitle,
        survey_number: form.surveyNumber || "",
        plot_area: form.plotAreaValue,
        plot_area_unit: form.plotAreaUnit?.toLowerCase() || "sq ft",
        facing_direction: form.plotFacing,
        is_corner_plot: form.cornerPlot === "Yes" ? 1 : 0,
        is_gated_community: form.gatedCommunity === "Yes" ? 1 : 0,
        has_boundary_wall: form.boundaryWall === "Yes" ? 1 : 0,
        dtcp_approved: form.dtcpApproval === "Yes" ? 1 : 0,
        cmda_approved: form.cmdaApproval === "Yes" ? 1 : 0,
        rera_number: form.reraNumber || "",
        patta_available: form.pattaChitta === "Yes" ? 1 : 0,
        fmb_sketch_available: form.fmbSketch === "Yes" ? 1 : 0,
        tax_receipt_available: form.landTaxReceipt === "Yes" ? 1 : 0,
        soil_type: form.soilType || "",
        nearby_facilities: form.nearbyFacilities || "",
        distance_to_main_road: form.distanceToMainRoad || "",
        advantages: form.advantages || "",
        description: form.advantages || "",
        layout_pdf_available: form.layoutPdf === "Yes" ? 1 : 0
      };
      formData.append("plot_details", JSON.stringify(plotDetails));

      // Image management
      if (deletedImageIds.length > 0) {
        formData.append("deleted_image_ids", JSON.stringify(deletedImageIds));
      }

      // Primary Image selection (from plotPhotos[0])
      if (media.plotPhotos.length > 0) {
        const primaryImg = media.plotPhotos[0];
        if (primaryImg.existing && primaryImg.imageId) {
          formData.append("primary_image_id", primaryImg.imageId);
        } else {
          formData.append("new_primary_image_index", 0);
        }
      }

      // Images - combine all media types
      const allImages = [
        ...media.plotPhotos,
        ...media.layoutMap
      ];
      allImages.forEach((img) => {
        if (img.file) {
          formData.append("images", img.file);
        }
      });

      // Submit to backend
      let response;
      if (editingId) {
        response = await axiosInstancev1.put(`/properties/${editingId}`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } else {
        response = await axiosInstancev1.post("/properties", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }

      if (response.data.success) {
        toast.success(editingId ? "Property updated successfully!" : "Plot listing created successfully!");
        if (editingId) {
          cancelEdit();
          setActiveTab("posted");
        } else {
          setCreatedListingId(response.data.listingId);
          setShowDurationModal(true);
        }
      }
    } catch (error) {
      console.error("Error submitting plot:", error);
      toast.error(error.response?.data?.message || "Failed to create listing");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreateOrder = (plan) => {
    // After successful payment, redirect or reload
    window.location.reload();
  };


  return (
    <>
      <div className="min-h-screen bg-white px-1 xs:px-2 sm:px-3 py-4">
        <div className="w-full max-w-6xl mx-auto">
          {/* Tab Navigation */}
          <div className="mb-4 border-b border-gray-200 overflow-x-auto no-scrollbar">
            <nav className="-mb-px flex space-x-4 sm:space-x-6 min-w-max" aria-label="Tabs">
              <button
                type="button"
                onClick={() => setActiveTab('post')}
                className={`whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${activeTab === 'post'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
              >
                Post Property
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('posted')}
                className={`whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${activeTab === 'posted'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
              >
                Your Listings
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('enquiry')}
                className={`whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${activeTab === 'enquiry'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
              >
                Enquiry Data
              </button>
            </nav>
          </div>

          {/* Tab Content */}
          {activeTab === 'enquiry' ? (
            <CustomerEnquiries category="plot" />
          ) : activeTab === 'posted' ? (
            <PostedProperties onEdit={handleEdit} listingTypeFilter="Sale" />
          ) : (
            <form
              onSubmit={handleSubmit}
              className="bg-white rounded-[28px] border border-gray-200 shadow-sm px-5 py-5 sm:px-7 sm:py-7 lg:p-8 lg:rounded-3xl"
            >
              {/* Mobile header */}
              <div className="mb-4 lg:hidden"><GradientHeader step={step} totalSteps={TOTAL_STEPS} /></div>

              <div className="flex flex-col lg:flex-row lg:gap-10">
                {/* LEFT SIDEBAR – Desktop sticky */}
                <aside className="hidden lg:flex lg:flex-col lg:w-1/3 lg:pr-6 lg:border-r lg:border-gray-100 lg:sticky lg:top-6 lg:self-start">
                  <GradientHeader step={step} totalSteps={TOTAL_STEPS} />

                  <div className="mt-6 space-y-4">
                    <h1 className="text-xl font-semibold text-gray-900">
                      {STEPS_META.find((s) => s.id === step)?.title}
                    </h1>
                    <p className="text-sm text-gray-500">
                      {STEPS_META.find((s) => s.id === step)?.desc}
                    </p>
                  </div>

                  {/* Animated step indicators */}
                  <ul className="mt-6 space-y-2">
                    {STEPS_META.map((s) => {
                      const isActive = s.id === step;
                      const isCompleted = s.id < step;
                      return (
                        <li key={s.id}>
                          <button
                            type="button"
                            onClick={() => setStep(s.id)}
                            className={`w-full flex items-center gap-3 rounded-xl px-3 py-2 text-left transition-all duration-300
                            ${isActive
                                ? "bg-blue-50 text-blue-700 shadow-sm scale-[1.02]"
                                : isCompleted
                                  ? "bg-green-50 text-green-700"
                                  : "text-gray-600 hover:bg-gray-50"
                              }`}
                          >
                            <div className="flex items-center justify-center">
                              {isCompleted ? (
                                <CheckCircleIcon className="w-5 h-5 text-green-500" />
                              ) : (
                                <div
                                  className={`w-5 h-5 flex items-center justify-center rounded-full border text-[11px] font-semibold
                                ${isActive
                                      ? "border-blue-500 text-blue-600 bg-white"
                                      : "border-gray-300 text-gray-500 bg-white"
                                    }`}
                                >
                                  {s.id}
                                </div>
                              )}
                            </div>
                            <div className="flex-1">
                              <p className="text-xs font-semibold">
                                {s.label}
                              </p>
                              <p className="text-[11px] text-gray-500 truncate">
                                {s.title}
                              </p>
                            </div>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </aside>

                {/* RIGHT CONTENT – Steps */}
                <div className="w-full lg:w-2/3 lg:pl-6 space-y-5">
                  {/* STEP 1 – Basic Details & Approvals */}
                  {step === 1 && (
                    <StepCard
                      stepNumber={1}
                      title="Basic Details & Approvals"
                      description="Core info and approval status."
                      isOpen={openSections[1]}
                      toggle={() => setOpenSections(prev => ({ ...prev, 1: !prev[1] }))}
                    >
                      <div className="space-y-4">
                        {/* Listing Type */}
                        <div>
                          <p className="text-xs font-semibold text-gray-700 mb-2">
                            Listing Type
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {LISTING_TYPES.map((lt) => {
                              const active = form.listingType === lt;
                              return (
                                <button
                                  type="button"
                                  key={lt}
                                  onClick={() =>
                                    updateField("listingType", lt)
                                  }
                                  className={`px-3 py-1.5 rounded-full text-xs border transition-all
                                  ${active
                                      ? "bg-blue-600 text-white border-blue-600"
                                      : "bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100"
                                    }`}
                                >
                                  {lt}
                                </button>
                              );
                            })}
                          </div>
                          {errors.listingType && (
                            <p className="text-[11px] text-red-500 mt-1">
                              {errors.listingType}
                            </p>
                          )}
                        </div>

                        {/* Plot Title */}
                        <div>
                          <p className="text-xs font-semibold text-gray-700 mb-1.5">
                            Plot Title
                          </p>
                          <input
                            type="text"
                            className="w-full rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none"
                            placeholder="Premium DTCP Plot near bypass road"
                            value={form.plotTitle}
                            onChange={(e) =>
                              updateField(
                                "plotTitle",
                                e.target.value
                              )
                            }
                          />
                          {errors.plotTitle && (
                            <p className="text-[11px] text-red-500 mt-1">
                              {errors.plotTitle}
                            </p>
                          )}
                        </div>

                        {/* Plot/Land Type */}
                        <div>
                          <p className="text-xs font-semibold text-gray-700 mb-2">
                            {plotMode === 'Land' ? 'Land Type' : 'Plot Type'}
                          </p>
                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                            {(plotMode === 'Land' ? LAND_TYPES : PLOT_TYPES).map((pt) => {
                              const active = form.plotType === pt.id;
                              const Icon = pt.icon;
                              return (
                                <button
                                  type="button"
                                  key={pt.id}
                                  onClick={() =>
                                    updateField("plotType", pt.id)
                                  }
                                  className={`flex flex-col items-center justify-center rounded-2xl border px-3 py-3 text-xs sm:text-sm transition-all
                                  ${active
                                      ? "border-blue-500 bg-blue-50 text-blue-700 shadow-sm"
                                      : "border-gray-200 bg-gray-50 text-gray-700 hover:bg-gray-100"
                                    }`}
                                >
                                  <Icon className="w-6 h-6 text-blue-600 mb-1" />
                                  <span className="font-medium text-center">
                                    {pt.label}
                                  </span>
                                </button>
                              );
                            })}
                          </div>
                          {errors.plotType && (
                            <p className="text-[11px] text-red-500 mt-1">
                              {errors.plotType}
                            </p>
                          )}
                        </div>

                        {/* Description */}
                        <div>
                          <p className="text-xs font-semibold text-gray-700 mb-1.5">
                            Description
                          </p>
                          <RichTextEditor
                            placeholder="Describe your plot..."
                            value={form.description}
                            onChange={(content) => updateField("description", content)}
                            height={200}
                          />
                        </div>

                        {/* Price */}
                        <div>
                          <p className="flex items-center gap-1.5 text-xs font-semibold text-gray-700 mb-1.5">
                            <BanknotesIcon className="w-4 h-4 text-blue-600" />
                            Price
                          </p>
                          <input
                            type="number"
                            className="w-full rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none"
                            placeholder="e.g. 1500000"
                            value={form.price}
                            onChange={(e) =>
                              updateField("price", e.target.value)
                            }
                          />
                          {errors.price && (
                            <p className="text-[11px] text-red-500 mt-1">
                              {errors.price}
                            </p>
                          )}
                        </div>

                        {/* Separator for Approvals */}
                        <div className="pt-4 border-t border-gray-100">
                          <p className="flex items-center gap-1.5 text-xs font-semibold text-gray-700 mb-3">
                            <ShieldCheckIcon className="w-4 h-4 text-blue-600" />
                            Approvals & Documents
                          </p>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {APPROVAL_FIELDS.map((field) => (
                              <div key={field.id} className="flex justify-between items-center bg-gray-50 px-3 py-2 rounded-xl border border-gray-200">
                                <span className="text-xs text-gray-600">{field.label}</span>
                                <div className="flex bg-white rounded-lg border border-gray-200 p-0.5">
                                  {["Yes", "No"].map((opt) => (
                                    <button
                                      type="button"
                                      key={opt}
                                      onClick={() => updateField(field.id, opt)}
                                      className={`px-3 py-1 rounded-md text-[10px] sm:text-xs transition-all ${form[field.id] === opt
                                        ? "bg-blue-600 text-white shadow-sm"
                                        : "text-gray-500 hover:text-gray-700"
                                        }`}
                                    >
                                      {opt}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </StepCard>
                  )}


                  {/* STEP 2 – Specifications */}
                  {step === 2 && (
                    <StepCard
                      stepNumber={2}
                      title="Plot Specifications"
                      description="Describe area, facing, and specific options."
                      isOpen={openSections[2]}
                      toggle={() => setOpenSections(prev => ({ ...prev, 2: !prev[2] }))}
                    >
                      <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <p className="flex items-center gap-1.5 text-xs font-semibold text-gray-700 mb-1.5">
                              <ArrowsPointingOutIcon className="w-4 h-4 text-blue-600" />
                              Plot Area
                            </p>
                            <div className="flex gap-2">
                              <input
                                type="number"
                                className="w-full rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none"
                                placeholder="e.g. 1200"
                                value={form.plotAreaValue}
                                onChange={(e) => updateField("plotAreaValue", e.target.value)}
                              />
                              <select
                                className="rounded-2xl border border-gray-200 bg-gray-50 px-2 py-3 text-xs outline-none"
                                value={form.plotAreaUnit}
                                onChange={(e) => updateField("plotAreaUnit", e.target.value)}
                              >
                                {PLOT_AREA_UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                              </select>
                            </div>
                            {errors.plotAreaValue && <p className="text-[11px] text-red-500 mt-1">{errors.plotAreaValue}</p>}
                          </div>

                          <div>
                            <p className="text-xs font-semibold text-gray-700 mb-1.5">Facing</p>
                            <input
                              type="text"
                              className="w-full rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none"
                              placeholder="e.g. North"
                              value={form.plotFacing}
                              onChange={(e) => updateField("plotFacing", e.target.value)}
                            />
                            {errors.plotFacing && <p className="text-[11px] text-red-500 mt-1">{errors.plotFacing}</p>}
                          </div>
                        </div>

                        {/* Specification Options */}
                        <div>
                          <p className="text-xs font-semibold text-gray-700 mb-1.5">Specification Options</p>
                          <div className="flex flex-wrap gap-2">
                            {["Road side plot", "Back side plot", "Center plot", "Front side plot"].map(opt => (
                              <button
                                type="button"
                                key={opt}
                                onClick={() => updateField("specificationOption", opt)}
                                className={`px-3 py-1.5 rounded-full text-xs border transition-all
                           ${form.specificationOption === opt ? "bg-blue-600 text-white border-blue-600" : "bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100"}
                           `}
                              >
                                {opt}
                              </button>
                            ))}
                          </div>
                        </div>

                        {/* Corner / Gated / Boundary */}
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                          {/* Corner Plot */}
                          <div>
                            <p className="text-xs font-semibold text-gray-700 mb-1.5">Corner Plot</p>
                            <div className="flex gap-2">
                              {YES_NO.map((v) => (
                                <button
                                  key={v}
                                  type="button"
                                  onClick={() => updateField("cornerPlot", v)}
                                  className={`flex-1 px-3 py-1.5 rounded-2xl text-xs border transition
                            ${form.cornerPlot === v ? "bg-blue-600 text-white border-blue-600" : "bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100"}`}
                                >
                                  {v}
                                </button>
                              ))}
                            </div>
                          </div>
                          {/* Gated Community */}
                          <div>
                            <p className="text-xs font-semibold text-gray-700 mb-1.5">Gated Community</p>
                            <div className="flex gap-2">
                              {YES_NO.map((v) => (
                                <button
                                  key={v}
                                  type="button"
                                  onClick={() => updateField("gatedCommunity", v)}
                                  className={`flex-1 px-3 py-1.5 rounded-2xl text-xs border transition
                            ${form.gatedCommunity === v ? "bg-blue-600 text-white border-blue-600" : "bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100"}`}
                                >
                                  {v}
                                </button>
                              ))}
                            </div>
                          </div>
                          {/* Boundary Wall */}
                          <div>
                            <p className="text-xs font-semibold text-gray-700 mb-1.5">Boundary Wall</p>
                            <div className="flex gap-2">
                              {YES_NO.map((v) => (
                                <button
                                  key={v}
                                  type="button"
                                  onClick={() => updateField("boundaryWall", v)}
                                  className={`flex-1 px-3 py-1.5 rounded-2xl text-xs border transition
                            ${form.boundaryWall === v ? "bg-blue-600 text-white border-blue-600" : "bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100"}`}
                                >
                                  {v}
                                </button>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    </StepCard>
                  )}

                  {/* STEP 3 - Media (Renamed step, removed drone, kept layout as image) */}
                  {step === 3 && (
                    <StepCard
                      stepNumber={3}
                      title="Media Upload"
                      description="Add photos and layout maps."
                      isOpen={openSections[3]}
                      toggle={() => setOpenSections(prev => ({ ...prev, 3: !prev[3] }))}
                    >
                      <div className="space-y-6">
                        {/* Photos */}
                        <div>
                          <p className="flex items-center gap-1.5 text-xs font-semibold text-gray-700 mb-3">
                            <PhotoIcon className="w-4 h-4 text-blue-600" />
                            Plot Photos
                            <span className="text-gray-400 font-normal ml-1">(Max 10)</span>
                          </p>
                          <p className="text-[11px] text-gray-500 mb-2">Upload clear photos of the plot from different angles.</p>
                          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
                            {media.plotPhotos.map((img, idx) => (
                              <div
                                key={idx}
                                onClick={() => setAsMain('plotPhotos', idx)}
                                className={`relative aspect-square rounded-xl overflow-hidden border-2 cursor-pointer transition-all duration-200 ${idx === 0 ? "border-blue-600 shadow-md scale-[1.02]" : "border-gray-200 hover:border-blue-400"}`}
                                title={idx === 0 ? "Banner Image" : "Click to set as Banner"}
                              >
                                <img src={img.previewUrl} alt={`Upload ${idx + 1}`} className="w-full h-full object-cover" />

                                {/* Banner Badge */}
                                {idx === 0 ? (
                                  <div className="absolute top-1 left-1 bg-blue-600 text-[8px] text-white px-2 py-0.5 rounded-full font-bold shadow-sm z-10">
                                    Banner
                                  </div>
                                ) : (
                                  <div className="absolute top-1 left-1 bg-white/90 text-blue-600 text-[8px] px-1.5 py-0.5 rounded-full font-bold opacity-0 group-hover:opacity-100 transition-opacity z-10 border border-blue-100 italic">
                                    Set Banner
                                  </div>
                                )}

                                <div className="absolute top-1 right-1 flex flex-col gap-1 z-20">
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      removeMedia('plotPhotos', idx);
                                    }}
                                    className="p-1 bg-red-500/90 text-white rounded-full hover:bg-red-600 transition-colors opacity-0 group-hover:opacity-100"
                                    title="Remove photo"
                                  >
                                    <TrashIcon className="w-3 h-3" />
                                  </button>

                                  {idx !== 0 && (
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setAsMain('plotPhotos', idx);
                                      }}
                                      className="p-1 bg-blue-600/90 text-white rounded-full hover:bg-blue-700 transition-colors opacity-0 group-hover:opacity-100"
                                      title="Set as Banner"
                                    >
                                      <CheckCircleIcon className="w-3 h-3" />
                                    </button>
                                  )}
                                </div>
                              </div>
                            ))}
                            {media.plotPhotos.length < 10 && (
                              <label className="aspect-square rounded-xl border-2 border-dashed border-gray-300 flex flex-col items-center justify-center cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition-colors">
                                <PhotoIcon className="w-6 h-6 text-gray-400 mb-1" />
                                <span className="text-[10px] text-gray-500 font-medium">+ Add</span>
                                <input type="file" multiple accept="image/*" className="hidden" onChange={(e) => handleMediaUpload('plotPhotos', e.target.files)} />
                              </label>
                            )}
                          </div>
                        </div>

                        {/* Layout Map (as image upload) */}
                        <div>
                          <p className="flex items-center gap-1.5 text-xs font-semibold text-gray-700 mb-3">
                            <MapPinIcon className="w-4 h-4 text-blue-600" />
                            Layout Map
                          </p>
                          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
                            {media.layoutMap.map((img, idx) => (
                              <div key={idx} className="relative aspect-square rounded-xl overflow-hidden border border-gray-200 group">
                                <img src={img.previewUrl} alt={`Layout ${idx + 1}`} className="w-full h-full object-cover" />
                                <button
                                  type="button"
                                  onClick={() => removeMedia('layoutMap', idx)}
                                  className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                                >
                                  <TrashIcon className="w-3 h-3" />
                                </button>
                              </div>
                            ))}
                            {media.layoutMap.length < 1 && (
                              <label className="aspect-square rounded-xl border-2 border-dashed border-gray-300 flex flex-col items-center justify-center cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition-colors">
                                <PhotoIcon className="w-6 h-6 text-gray-400 mb-1" />
                                <span className="text-[10px] text-gray-500 font-medium">Upload Map</span>
                                <input type="file" accept="image/*" className="hidden" onChange={(e) => handleMediaUpload('layoutMap', e.target.files)} />
                              </label>
                            )}
                          </div>
                        </div>
                      </div>
                    </StepCard>
                  )}

                  {/* Step 4 - Additional */}
                  {step === 4 && (
                    <StepCard
                      stepNumber={4}
                      title="Additional Information"
                      description="Explain nearby facilities."
                      isOpen={openSections[4]}
                      toggle={() => setOpenSections(prev => ({ ...prev, 4: !prev[4] }))}
                    >
                      <div className="space-y-4">
                        <div>
                          <p className="text-xs font-semibold text-gray-700 mb-1.5">Nearby Facilities</p>
                          <RichTextEditor
                            placeholder="Schools, colleges, etc."
                            value={form.nearbyFacilities}
                            onChange={(content) => updateField("nearbyFacilities", content)}
                            height={150}
                          />
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div>
                            <p className="text-xs font-semibold text-gray-700 mb-1.5">Distance to Main Road</p>
                            <input type="text" className="w-full rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm" value={form.distanceToMainRoad} onChange={(e) => updateField("distanceToMainRoad", e.target.value)} />
                          </div>
                          <div>
                            <p className="text-xs font-semibold text-gray-700 mb-1.5">Soil Type</p>
                            <input type="text" className="w-full rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm" value={form.soilType} onChange={(e) => updateField("soilType", e.target.value)} />
                          </div>
                        </div>

                      </div>
                    </StepCard>
                  )}

                  {/* Step 5 - Location (Moved from Step 2) */}
                  {step === 5 && (
                    <StepCard
                      stepNumber={5}
                      title="Location Details"
                      description="Share accurate location."
                      isOpen={openSections[5]}
                      toggle={() => setOpenSections(prev => ({ ...prev, 5: !prev[5] }))}
                    >
                      <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <p className="text-xs font-semibold text-gray-700 mb-1.5">City</p>
                            <input type="text" placeholder="City" value={form.city} onChange={(e) => updateField("city", e.target.value)} className="w-full rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm" />
                            {errors.city && <p className="text-[11px] text-red-500 mt-1">{errors.city}</p>}
                          </div>
                          <div>
                            <p className="text-xs font-semibold text-gray-700 mb-1.5">Locality</p>
                            <input type="text" placeholder="Locality" value={form.locality} onChange={(e) => updateField("locality", e.target.value)} className="w-full rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm" />
                            {errors.locality && <p className="text-[11px] text-red-500 mt-1">{errors.locality}</p>}
                          </div>
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-gray-700 mb-1.5">Plot Address</p>
                          <input type="text" placeholder="Address" value={form.plotAddress} onChange={(e) => updateField("plotAddress", e.target.value)} className="w-full rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm" />
                          {errors.plotAddress && <p className="text-[11px] text-red-500 mt-1">{errors.plotAddress}</p>}
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <p className="text-xs font-semibold text-gray-700 mb-1.5">Pincode</p>
                            <input type="text" value={form.pincode} onChange={(e) => updateField("pincode", e.target.value)} className="w-full rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm" />
                          </div>
                          <div>
                            <p className="text-xs font-semibold text-gray-700 mb-1.5">Map Pin</p>
                            <input type="text" value={form.mapPin} onChange={(e) => updateField("mapPin", e.target.value)} className="w-full rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm" />
                          </div>
                        </div>
                        <LocationPicker
                          onLocationSelect={({ latitude, longitude }) => {
                            updateField("latitude", latitude);
                            updateField("longitude", longitude);
                          }}
                          initialLat={form.latitude || 8.7139}
                          initialLng={form.longitude || 77.7567}
                        />
                      </div>
                    </StepCard>
                  )}

                  {/* Step 6 - Contact */}
                  {step === 6 && (
                    <StepCard
                      stepNumber={6}
                      title="Contact Details"
                      description="Who should buyers contact?"
                      isOpen={openSections[6]}
                      toggle={() => setOpenSections(prev => ({ ...prev, 6: !prev[6] }))}
                    >
                      <div className="space-y-4">
                        {/* Quick summary card */}
                        <div className="rounded-2xl border border-gray-100 bg-gray-50 px-4 py-3 text-xs text-gray-700 space-y-1">
                          <p className="font-semibold text-gray-900 text-sm flex items-center gap-1.5">
                            <HomeIcon className="w-4 h-4 text-blue-600" />
                            {form.plotTitle || "Untitled Plot"}
                          </p>
                          <p>
                            {form.city && form.locality
                              ? `${form.locality}, ${form.city}`
                              : "Location not fully filled yet"}
                          </p>
                          <p>
                            {form.price
                              ? `₹${form.price}`
                              : "Price not set"}
                          </p>
                        </div>

                        <input type="text" placeholder="Name" value={form.ownerName} onChange={(e) => updateField("ownerName", e.target.value)} className="w-full rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm" />
                        {errors.ownerName && <p className="text-[11px] text-red-500 mt-1">{errors.ownerName}</p>}
                        <input type="tel" placeholder="Mobile" value={form.mobileNumber} onChange={(e) => updateField("mobileNumber", e.target.value)} className="w-full rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm" />
                        {errors.mobileNumber && <p className="text-[11px] text-red-500 mt-1">{errors.mobileNumber}</p>}
                        <input type="email" placeholder="Email" value={form.email} onChange={(e) => updateField("email", e.target.value)} className="w-full rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm" />
                        <input type="text" placeholder="Preferred Visit Time" value={form.preferredVisitTime} onChange={(e) => updateField("preferredVisitTime", e.target.value)} className="w-full rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm" />
                      </div>
                    </StepCard>
                  )}
                  {/* NAV BAR */}
                  <div className="mt-4 flex items-center justify-between gap-2 pt-2 border-t border-gray-100">
                    <button
                      type="button"
                      onClick={goBack}
                      disabled={step === 1}
                      className={`text-xs font-medium px-4 py-2 rounded-full border transition
                      ${step === 1
                          ? "border-gray-100 text-gray-300 cursor-not-allowed"
                          : "border-gray-200 text-gray-700 hover:bg-gray-50"
                        }`}
                    >
                      Back
                    </button>

                    {step < TOTAL_STEPS ? (
                      <button
                        type="button"
                        onClick={goNext}
                        className="ml-auto text-xs sm:text-sm font-semibold px-5 py-2.5 rounded-full bg-blue-600 text-white shadow-sm hover:bg-blue-700 transition"
                      >
                        Next
                      </button>
                    ) : (
                      <button
                        type="submit"
                        disabled={submitting}
                        className="ml-auto text-xs sm:text-sm font-semibold px-6 py-2.5 rounded-full bg-green-600 text-white shadow-sm hover:bg-green-700 transition disabled:bg-gray-400 disabled:cursor-not-allowed"
                      >
                        {submitting ? "Publishing..." : "Publish Listing"}
                      </button>
                    )}
                  </div>
                </div>
              </div >
            </form >
          )}
        </div >
      </div >
      {showDurationModal && (
        <DurationSelectionModal
          isOpen={showDurationModal}
          onClose={() => setShowDurationModal(false)}
          listingId={createdListingId}
          onSuccess={handleCreateOrder}
          planType="PLOT"
        />
      )}
    </>
  );
};

export default PropertyPost;
